import {
  AddRounded,
  AssignmentOutlined,
  CloudSyncOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { lazy, Suspense, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { isNil } from "lodash";
import LoadingFallback from "../../../components/LoadingFallback";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";
import { useLoanTermsHook } from "../../../hooks/useLoanTermsHook";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { grey } from "@mui/material/colors";

// Lazy load heavy components
const DropdownButton = lazy(
  () => import("../../../components/ui/buttons/DropdownButton"),
);

export const SYNC_TYPES = {
  // Create a brand-new Salesforce opportunity from the loan (no external id yet).
  CREATE_OPPORTUNITY: "create-opportunity",
  // Sync an existing Salesforce opportunity (loan already has a sync_loan_id).
  SALESFORCE_SYNC: "salesforce-sync",
  BOX_SYNC: "box-sync",
};
const TYPES = SYNC_TYPES;

// While Salesforce sync is being validated, the External Sync button is gated
// to a small allowlist (Jimmy, Chris, Holden) rather than all admins. Compared
// case-insensitively (Holden's stored email is mixed-case).
const LoanSyncDropdownButton = () => {
  const [modalType, setModalType] = useState(null);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const handleUploadClick = useCallback((type) => {
    setModalType(type);
  }, []);

  // A loan is "linked" to Salesforce once it has an external sync id.
  const hasSalesforceLink = !isNil(loanDrilldown?.sync_loan_id);

  const options = [
    {
      // Create is only meaningful before the loan is linked to an opportunity.
      label: hasSalesforceLink
        ? "Create Salesforce Opportunity (Already Linked)"
        : "Create Salesforce Opportunity",
      onClick: () => handleUploadClick(TYPES.CREATE_OPPORTUNITY),
      disabled: hasSalesforceLink,
      icon: <AddRounded sx={{ color: grey[900] }} />,
    },
    {
      // Push the latest LOS loan data to the existing Salesforce opportunity.
      label: hasSalesforceLink
        ? "Push to Salesforce"
        : "Push to Salesforce (External ID Missing)",
      onClick: () => handleUploadClick(TYPES.SALESFORCE_SYNC),
      disabled: !hasSalesforceLink,
      icon: <CloudSyncOutlined className="thin" />,
    },
    {
      label: "Sync With Box",
      onClick: () => handleUploadClick(TYPES.BOX_SYNC),
      disabled: true,
      icon: <AssignmentOutlined className="thin" />,
    },
  ];

  // Salesforce sync is available to everyone.
  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        {modalType === TYPES.CREATE_OPPORTUNITY && (
          <CreateSalesforceOpportunityModal
            onClose={() => setModalType(null)}
          />
        )}

        {modalType === TYPES.SALESFORCE_SYNC && (
          <SalesforceSyncConfirmationModal onClose={() => setModalType(null)} />
        )}

        {modalType === TYPES.BOX_SYNC && (
          <BoxSyncConfirmationModal onClose={() => setModalType(null)} />
        )}

        {/* Icon-only button with a hover tooltip — the "External Sync" text was
            dropped to condense the title-row controls. Borderless to match the
            sibling title-row icon buttons (edit / tags / favorite / refresh). */}
        <DropdownButton
          size="small"
          icon={<SyncOutlined fontSize="small" />}
          iconTooltip="External Sync"
          sx={{ color: grey[700] }}
          options={options}
        />
      </Suspense>
    </>
  );
};

// Refetch the loan after a sync/create so the drilldown reflects the new
// sync_loan_id / last_integration_sync stamps.
//
// We refetch IN PLACE — we do NOT clear the loan first. Clearing nulls
// `loanDrilldown`, which trips the drilldown screen's "fetch when nil" effect
// and fires a second, redundant GET. We also leave the existing loan intact on
// failure (a transient refresh error shouldn't blank a loan we just synced).
const useRefreshLoan = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { getLoanById } = useUnderwritingHook();
  const dispatch = useDispatch();

  return useCallback(() => {
    getLoanById({
      loanId: loanDrilldown?._id,
      onSuccessFn: (data) => dispatch(loanDrilldownSet(data)),
    });
  }, [getLoanById, loanDrilldown?._id, dispatch]);
};

export const CreateSalesforceOpportunityModal = ({ onClose }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  // Creation runs through the same simplified sync endpoint as a push: when the
  // loan has no sync_loan_id yet, simple_salesforce_sync routes to the create
  // path (portal_to_sf_create_loan), which creates the opportunity and stamps
  // sync_loan_id + salesforce_opportunity_id back on the loan.
  const { loading, syncLoanWithSalesforceById } = useLoanTermsHook();
  const refreshLoan = useRefreshLoan();

  const onSubmit = () => {
    syncLoanWithSalesforceById({
      dealId: loanDrilldown?._id,
      onSuccessFn: () => {
        refreshLoan();
        onClose();
      },
    });
  };

  return (
    <ConfirmationDialog
      open
      loading={loading}
      onCancel={onClose}
      onConfirm={onSubmit}
      title={"Create Salesforce Opportunity"}
      message={
        <>
          This creates a <b>new Salesforce opportunity</b> from this loan's
          current details (borrowers, properties, terms) and links it back via{" "}
          <b>sync_loan_id</b>. It's <b>queued and runs in the background</b> —
          the link may take a moment to appear. Continue?
        </>
      }
      confirmText="Yes, Create Opportunity"
    />
  );
};

export const SalesforceSyncConfirmationModal = ({ onClose }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, syncLoanWithSalesforceById } = useLoanTermsHook();
  const refreshLoan = useRefreshLoan();

  const onSubmit = () => {
    syncLoanWithSalesforceById({
      dealId: loanDrilldown?._id,
      onSuccessFn: () => {
        refreshLoan();
        onClose();
      },
    });
  };

  return (
    <ConfirmationDialog
      open
      loading={loading}
      onCancel={onClose}
      onConfirm={onSubmit}
      title={"Push to Salesforce"}
      message={
        <>
          This pushes this loan's latest data from the LOS to the linked
          Salesforce loan (ID <b>{loanDrilldown?.sync_loan_id}</b>),
          overwriting those fields in Salesforce. The push is{" "}
          <b>queued and runs in the background</b> — it may take a moment to
          appear in Salesforce, and the loan's last-synced time updates once it
          completes. Continue?
        </>
      }
      confirmText="Yes, Push to Salesforce"
    />
  );
};

export const BoxSyncConfirmationModal = ({ onClose }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  return (
    <ConfirmationDialog
      open
      onCancel={onClose}
      onConfirm={onClose}
      title={"Sync With Box"}
      message={
        <>
          Box sync is not yet available for this loan
          {loanDrilldown?.sync_loan_id ? (
            <>
              {" "}
              (ID <b>{loanDrilldown?.sync_loan_id}</b>)
            </>
          ) : null}
          .
        </>
      }
      confirmText="Close"
    />
  );
};

export default LoanSyncDropdownButton;
